/**
 * RebalancerPage — Multi-Account Rebalancer
 *
 * Takes a target asset-class allocation and generates a concrete trade list
 * across Simon's discretionary accounts only (IG Trading, IG Smart wrapper
 * excluded as non-disc, HL, AJ Bell SIPP XO, Coinbase).
 *
 * Never recommends a trade against a non-discretionary account (Managed SIPP,
 * ISAs, Premium Bonds, Property, Liabilities).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Scale, AlertTriangle, ShieldCheck, RefreshCw, ArrowUp, ArrowDown, Sparkles,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, Cell,
} from 'recharts'
import { api, normalizeApiErrorDetail } from '@/lib/api'
import PageHeader from '@/components/ui/PageHeader'

// ── Types ────────────────────────────────────────────────────────────────────

interface RebalancerTrade {
  action: 'BUY' | 'SELL'
  ticker: string
  name: string
  account_id: number
  account_name: string
  asset_class: string
  qty: number | null
  value_gbp: number
  reasoning: string
}

interface RebalancerGap {
  asset_class: string
  current_pct: number
  target_pct: number
  gap_pct: number
  gap_gbp: number
  direction: 'increase' | 'reduce' | 'hold'
}

interface RebalancerTaxWarning {
  ticker: string
  name: string
  account: string
  trim_value_gbp: number
  est_realised_gain_gbp: number
  message: string
}

interface RebalancerAllocItem {
  asset_class: string
  current_pct?: number
  current_gbp?: number
  target_pct?: number
}

interface RebalancerPlan {
  total_portfolio_value: number
  eligible_account_count: number
  discretionary_only: boolean
  min_trade_gbp: number
  current_allocation: RebalancerAllocItem[]
  target_allocation: RebalancerAllocItem[]
  gaps: RebalancerGap[]
  trades: RebalancerTrade[]
  trade_count: number
  total_buys_gbp: number
  total_sells_gbp: number
  net_gbp: number
  tax_warnings: RebalancerTaxWarning[]
  summary_text: string
}

// ── Asset classes (must match backend _SUPPORTED_ASSET_CLASSES primary set) ──

const ASSET_CLASSES = [
  'UK Equity',
  'US Equity',
  'International Equity',
  'Bonds',
  'Crypto',
  'Cash',
] as const

type AssetClass = (typeof ASSET_CLASSES)[number]

const DEFAULT_TARGET: Record<AssetClass, number> = {
  'UK Equity': 25,
  'US Equity': 35,
  'International Equity': 15,
  'Bonds': 15,
  'Crypto': 5,
  'Cash': 5,
}

// Brand palette
const COLOURS: Record<string, string> = {
  'UK Equity': '#60a5fa',
  'US Equity': '#34d399',
  'International Equity': '#a78bfa',
  'Bonds': '#fbbf24',
  'Crypto': '#f472b6',
  'Cash': '#94a3b8',
  'Healthcare': '#22d3ee',
  'Technology': '#f97316',
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtSigned = (n: number) => {
  const abs = Math.abs(n)
  const s = abs.toLocaleString('en-GB', { maximumFractionDigits: 0 })
  return `${n >= 0 ? '+' : '−'}£${s}`
}

export default function RebalancerPage() {
  const [targets, setTargets] = useState<Record<AssetClass, number>>({ ...DEFAULT_TARGET })
  const [minTrade, setMinTrade] = useState(100)
  const [includeNonDisc, setIncludeNonDisc] = useState(false)

  const [plan, setPlan] = useState<RebalancerPlan | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [enqueuing, setEnqueuing] = useState(false)
  const [enqueueResult, setEnqueueResult] = useState<{
    count: number
    total: number
    skipped: number
    error?: string
  } | null>(null)

  const totalTarget = useMemo(
    () => Object.values(targets).reduce((a, b) => a + b, 0),
    [targets],
  )
  const balanced = Math.abs(totalTarget - 100) < 0.01

  const generatePlan = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.post<RebalancerPlan>('/api/rebalance/plan', {
        target_allocation: targets,
        include_non_discretionary: includeNonDisc,
        min_trade_gbp: minTrade,
      })
      setPlan(data)
    } catch (err: any) {
      setError(normalizeApiErrorDetail(err?.response?.data?.detail, err?.message || 'Failed to generate plan'))
      setPlan(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    generatePlan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setSingle = (cls: AssetClass, v: number) => {
    setTargets(prev => ({ ...prev, [cls]: Math.max(0, Math.min(100, v)) }))
  }

  const resetDefault = () => setTargets({ ...DEFAULT_TARGET })

  const sendToQueue = async () => {
    if (!plan || plan.trades.length === 0) return
    if (!confirm(`Send ${plan.trades.length} trades to the approval queue? Each still requires manual approval + execute before anything reaches the broker.`)) return
    setEnqueuing(true)
    setEnqueueResult(null)
    // Trades without a resolvable qty (unpriced holdings) can't be enqueued —
    // count them so the user sees "K skipped (no price)" instead of silence.
    const sendable = plan.trades.filter(t => t.qty != null && t.qty > 0 && t.account_id)
    const skipped = plan.trades.length - sendable.length
    try {
      // Build minimal EnqueueRequest objects from each sendable planned trade
      const body = {
        trades: sendable.map(t => ({
          account_id: t.account_id,
          ticker: t.ticker,
          action: t.action,
          quantity: Number(t.qty),
          reason: t.reasoning,
          estimated_value_gbp: t.value_gbp,
        })),
      }
      const { data } = await api.post<Array<any>>('/api/rebalance-queue/enqueue', body)
      setEnqueueResult({
        count: Array.isArray(data) ? data.length : body.trades.length,
        total: plan.trades.length,
        skipped,
      })
    } catch (err: any) {
      // 422 detail is a dict ({"errors": [...]}) — normalise before rendering,
      // a raw object as a React child white-screens the page.
      setEnqueueResult({
        count: 0,
        total: plan.trades.length,
        skipped,
        error: normalizeApiErrorDetail(err?.response?.data?.detail, err?.message || 'Enqueue failed'),
      })
    } finally {
      setEnqueuing(false)
    }
  }

  // Chart data: one row per asset class with target vs current %
  const chartData = useMemo(() => {
    if (!plan) return []
    const byClass: Record<string, { asset: string; current: number; target: number }> = {}
    for (const c of plan.current_allocation) {
      const k = c.asset_class
      byClass[k] = byClass[k] || { asset: k, current: 0, target: 0 }
      byClass[k].current = c.current_pct ?? 0
    }
    for (const t of plan.target_allocation) {
      const k = t.asset_class
      byClass[k] = byClass[k] || { asset: k, current: 0, target: 0 }
      byClass[k].target = t.target_pct ?? 0
    }
    return Object.values(byClass).sort((a, b) => b.target + b.current - a.target - a.current)
  }, [plan])

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Multi-Account Rebalancer"
        icon={Scale}
        subtitle="Set target weights, get a concrete trade list across discretionary accounts only."
        actions={
          <div className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald/40 bg-emerald/10 text-emerald-light">
            <ShieldCheck size={13} aria-hidden />
            Discretionary only — manager-controlled accounts excluded
          </div>
        }
      />

      {/* Target-allocation editor */}
      <div className="card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="panel-title flex items-center gap-2">
            <Sparkles size={14} className="text-blue-light" aria-hidden />
            Target Allocation
          </h2>
          <div className="flex items-center gap-3 text-xs">
            <span className={clsx(
              'font-semibold',
              balanced ? 'text-emerald-light' : 'text-amber',
            )}>
              Total: {totalTarget.toFixed(1)}%
              {!balanced && ' (must sum to 100%)'}
            </span>
            <button type="button" onClick={resetDefault} className="btn-secondary text-xs">
              Reset
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ASSET_CLASSES.map(cls => (
            <div key={cls} className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-text-secondary flex items-center gap-2">
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: COLOURS[cls] }}
                  />
                  {cls}
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={targets[cls]}
                    onChange={(e) => setSingle(cls, Number(e.target.value))}
                    className="input w-16 px-2 py-1 text-xs text-right"
                  />
                  <span className="text-xs text-text-muted">%</span>
                </div>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={targets[cls]}
                onChange={(e) => setSingle(cls, Number(e.target.value))}
                className="w-full accent-blue-light"
              />
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-5 pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-text-secondary">Min trade size</label>
            <input
              type="number"
              min={0}
              step={50}
              value={minTrade}
              onChange={(e) => setMinTrade(Number(e.target.value))}
              className="input w-24 px-2 py-1 text-xs text-right"
            />
            <span className="text-xs text-text-muted">£</span>
          </div>
          <label className="flex items-center gap-2 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={includeNonDisc}
              onChange={(e) => setIncludeNonDisc(e.target.checked)}
              className="rounded border-border bg-surface-card"
            />
            Include non-discretionary accounts (dangerous)
          </label>
        </div>

        <button
          type="button"
          onClick={generatePlan}
          disabled={loading || !balanced}
          className="btn-primary mt-5 w-full sm:w-auto gap-2"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} aria-hidden />
          {loading ? 'Generating plan…' : 'Generate Plan'}
        </button>
      </div>

      {error && (
        <div className="card p-4 border border-rose/40 bg-rose/5 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Headline stats */}
      {plan && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
          <div className="stat-card">
            <p className="stat-label">Eligible Portfolio</p>
            <p className="stat-hero">{fmt(plan.total_portfolio_value)}</p>
            <p className="text-xs text-text-muted">
              {plan.eligible_account_count} account{plan.eligible_account_count === 1 ? '' : 's'}
              {plan.discretionary_only ? ' (discretionary)' : ' (incl. non-disc)'}
            </p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Total Sells</p>
            <p className="stat-value text-rose-light">{fmt(plan.total_sells_gbp)}</p>
            <p className="text-xs text-text-muted">
              {plan.trades.filter(t => t.action === 'SELL').length} trim orders
            </p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Total Buys</p>
            <p className="stat-value text-emerald-light">{fmt(plan.total_buys_gbp)}</p>
            <p className="text-xs text-text-muted">
              {plan.trades.filter(t => t.action === 'BUY').length} buy orders
            </p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Net Flow</p>
            <p className={clsx(
              'stat-value',
              Math.abs(plan.net_gbp) < 1 ? 'text-text-primary'
                : plan.net_gbp > 0 ? 'text-amber' : 'text-blue-light',
            )}>
              {fmtSigned(plan.net_gbp)}
            </p>
            <p className="text-xs text-text-muted">target is ~£0</p>
          </div>
        </div>
      )}

      {/* Summary callout */}
      {plan && (
        <div className="card p-4 text-sm text-text-secondary border-l-4 border-blue/40">
          {plan.summary_text}
        </div>
      )}

      {/* Gap visualisation */}
      {plan && chartData.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">
            Allocation Gap — Current vs Target
          </h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis
                  dataKey="asset"
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-12}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: 8,
                    fontSize: 12, color: 'rgb(var(--text-primary))'}}
                  formatter={(v: any) => `${(v as number).toFixed(1)}%`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="current" name="Current %" fill="#6366f1">
                  {chartData.map((entry) => (
                    <Cell key={`c-${entry.asset}`} fill={COLOURS[entry.asset] ?? '#6366f1'} fillOpacity={0.45} />
                  ))}
                </Bar>
                <Bar dataKey="target" name="Target %" fill="#34d399">
                  {chartData.map((entry) => (
                    <Cell key={`t-${entry.asset}`} fill={COLOURS[entry.asset] ?? '#34d399'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Gap table */}
          <div className="mt-4 table-responsive overflow-x-auto">
            <table className="table-pro w-full text-xs">
              <thead>
                <tr>
                  <th>Asset Class</th>
                  <th className="!text-right">Current</th>
                  <th className="!text-right">Target</th>
                  <th className="!text-right">Gap (pp)</th>
                  <th className="!text-right">Gap (£)</th>
                  <th className="pl-4">Direction</th>
                </tr>
              </thead>
              <tbody>
                {plan.gaps.map(g => (
                  <tr key={g.asset_class}>
                    <td className="text-text-primary font-medium">
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                        style={{ backgroundColor: COLOURS[g.asset_class] ?? '#6b7280' }}
                      />
                      {g.asset_class}
                    </td>
                    <td className="text-right text-text-secondary">{(g.current_pct ?? 0).toFixed(1)}%</td>
                    <td className="text-right text-text-secondary">{(g.target_pct ?? 0).toFixed(1)}%</td>
                    <td className="text-right">
                      <span className={clsx(
                        g.gap_pct > 0 ? 'delta-chip-pos' : g.gap_pct < 0 ? 'delta-chip-neg' : 'text-text-muted',
                      )}>
                        {(g.gap_pct ?? 0) > 0 ? '+' : ''}{(g.gap_pct ?? 0).toFixed(1)}
                      </span>
                    </td>
                    <td className={clsx(
                      'text-right font-mono',
                      g.gap_gbp > 0 ? 'text-emerald-light' : g.gap_gbp < 0 ? 'text-rose-light' : 'text-text-muted',
                    )}>
                      {fmtSigned(g.gap_gbp)}
                    </td>
                    <td className="pl-4 text-text-secondary uppercase text-[10px] tracking-wide">
                      {g.direction === 'increase' && <ArrowUp size={12} className="inline text-emerald" />}
                      {g.direction === 'reduce' && <ArrowDown size={12} className="inline text-rose-light" />}
                      {' '}{g.direction}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tax warnings */}
      {plan && plan.tax_warnings.length > 0 && (
        <div className="card p-5 border border-amber/40 bg-amber/5">
          <h2 className="panel-title !text-amber-light flex items-center gap-2 mb-3">
            <AlertTriangle size={14} aria-hidden />
            Tax Warnings ({plan.tax_warnings.length})
          </h2>
          <ul className="space-y-2 text-xs text-text-secondary">
            {plan.tax_warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber mt-0.5">•</span>
                <div>
                  <p className="text-text-primary font-medium">
                    {w.ticker} — {w.account}
                  </p>
                  <p className="text-text-muted">
                    Trim £{(w.trim_value_gbp ?? 0).toLocaleString('en-GB')} · est. realised gain{' '}
                    <span className="text-amber-light font-semibold">
                      £{(w.est_realised_gain_gbp ?? 0).toLocaleString('en-GB')}
                    </span>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Trade list */}
      {plan && plan.trades.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="panel-title">
              Trade List ({plan.trades.length})
            </h2>
            <div className="flex items-center gap-2">
              {enqueueResult && !enqueueResult.error && (
                <span className="text-xs text-emerald-light">
                  ✓ {enqueueResult.count} of {enqueueResult.total} sent to queue
                  {enqueueResult.skipped > 0 && (
                    <span className="text-amber"> — {enqueueResult.skipped} skipped (no price)</span>
                  )}
                  {' '}— <Link to="/app/rebalance-queue" className="underline">review →</Link>
                </span>
              )}
              {enqueueResult?.error && (
                <span className="text-xs text-rose-light">
                  Failed: {enqueueResult.error}
                  {enqueueResult.skipped > 0 && ` (${enqueueResult.skipped} unpriced trade${enqueueResult.skipped === 1 ? '' : 's'} would be skipped)`}
                </span>
              )}
              <button
                type="button"
                onClick={sendToQueue}
                disabled={enqueuing || plan.trades.length === 0}
                className="btn-secondary gap-2 text-xs"
                title="Create pending trades in the approval queue — nothing executes without explicit approval"
              >
                {enqueuing ? 'Sending…' : 'Send to Approval Queue'}
              </button>
            </div>
          </div>
          <div className="table-responsive overflow-x-auto">
            <table className="table-pro w-full text-xs">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Ticker</th>
                  <th>Asset Class</th>
                  <th>Account</th>
                  <th className="!text-right">Qty (est.)</th>
                  <th className="!text-right">Value</th>
                  <th className="pl-4">Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {plan.trades.map((t, i) => (
                  <tr key={i}>
                    <td>
                      <span className={clsx(
                        t.action === 'BUY' ? 'badge-green' : 'badge-red',
                        'uppercase tracking-wider',
                      )}>
                        {t.action}
                      </span>
                    </td>
                    <td className="font-mono text-text-primary">{t.ticker}</td>
                    <td className="text-text-secondary">{t.asset_class}</td>
                    <td className="text-text-secondary">{t.account_name}</td>
                    <td className="text-right text-text-muted font-mono">
                      {t.qty !== null && t.qty !== undefined
                        ? t.qty.toLocaleString('en-GB', { maximumFractionDigits: 4 })
                        : '—'}
                    </td>
                    <td className="text-right font-mono text-text-primary">{fmt(t.value_gbp)}</td>
                    <td className="pl-4 text-text-muted text-[11px]">{t.reasoning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {plan && plan.trades.length === 0 && (
        <div className="card p-5 text-sm text-text-secondary text-center">
          Portfolio already matches your target allocation (within min trade size).
          No trades required.
        </div>
      )}
    </div>
  )
}
